import { ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { Key, useCallback, useMemo } from 'react';

import { format } from 'date-fns';
import { isImageExtension } from 'src/shared/utils/fileTypeUtils';

import { Image } from 'src/shared/components/ui';
import {
  ButtonV2,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import fileColumns from '../../constants/fileColumns';

interface ProductDetailFilesTabProps {
  files?: any[];
}

const ProductDetailFilesTab: React.FC<ProductDetailFilesTabProps> = ({
  files
}) => {
  const filteredFiles = useMemo(() => {
    return files?.filter(
      (file) => !file.file || !file.file.tags.includes('visibility:hidden')
    );
  }, [files]);

  const renderCell = useCallback((item: any, columnKey: Key) => {
    const file = item?.file;
    const fileName = file ? file?.name : item.name;
    const fileIsImage = isImageExtension(fileName);
    const fileDescription = file ? file?.description : item.caption;
    const fileUrl = file ? file?.url : item.url;

    switch (columnKey) {
      case 'file':
        return (
          <div className="flex items-center gap-2">
            <div className="size-12 flex items-center justify-center object-cover min-w-12 rounded-xl bg-gray-2 border border-gray-4 overflow-hidden">
              {fileIsImage ? (
                <Image
                  disableAnimation
                  disableSkeleton
                  className="size-12 object-cover rounded-xl"
                  src={fileUrl}
                  alt={item}
                />
              ) : (
                <DocumentIcon className="size-6" />
              )}
            </div>
            <div className="flex flex-col">
              <Typography variant="body-1" weight="medium">
                {fileName}
              </Typography>
              {fileDescription && (
                <Typography variant="body-3" weight="medium">
                  {fileDescription}
                </Typography>
              )}
            </div>
          </div>
        );

      case 'uploaded':
        return (
          <Typography variant="body-1" weight="medium">
            {item.upload_date ? format(item.upload_date, 'MMM dd, yyyy') : '--'}
          </Typography>
        );

      case 'action':
        return (
          <ButtonV2
            onClick={() => window.open(fileUrl, '_blank')}
            size="md"
            isIconOnly
            color="secondary"
            className="group-hover:!bg-gray-4"
          >
            <ArrowDownTrayIcon className="size-4" />
          </ButtonV2>
        );

      default:
        return '';
    }
  }, []);

  return (
    <div className="flex flex-col">
      <Typography
        className="flex items-center gap-2 h-[33px]"
        variant="h4"
        as="h2"
        weight="bold"
      >
        {filteredFiles && (
          <>
            {filteredFiles?.length}{' '}
            {filteredFiles?.length === 1 ? 'File' : 'Files'}
          </>
        )}
      </Typography>

      <Table aria-label="Files table">
        <TableHeader columns={fileColumns}>
          {(column) => (
            <TableColumn className="px-4" key={column.key}>
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          emptyContent={filteredFiles?.length === 0 && 'No files'}
          items={filteredFiles}
        >
          {(item) => (
            <TableRow
              onClick={() => {
                const file = item?.file;
                const fileUrl = file ? file?.url : item.url;

                window.open(fileUrl, '_blank');
              }}
              className="group"
              key={item.id ?? item.name}
            >
              {(columnKey) => (
                <TableCell>{renderCell(item, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductDetailFilesTab;
